import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const rouletteModes = [
  {
    key: 'zodiacal',
    icon: '👥',
  },
  {
    key: 'compatibles',
    icon: '💗',
  },
  {
    key: 'opuestos',
    icon: '⚡',
  },
];

export default function RouletteHubScreen() {
  const { t } = useTranslation(['home', 'common']);
  const navigate = useNavigate();

  const handlers = {
    zodiacal: () => navigate('/zodiac-roulette'),
    compatibles: () => navigate('/filtered-roulette/compatibles'),
    opuestos: () => navigate('/filtered-roulette/opuestos'),
  };

  return (
    <div id="rouletteHubRoot" style={styles.root}>
      <style>{`
        @keyframes rouletteHubSpin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        #rouletteHubRoot::before{
          content:"";
          position:fixed;
          inset:0;
          z-index:0;
          pointer-events:none;
          background-image:
            radial-gradient(circle at 10% 8%, rgba(255,255,255,.85) 0 1px, transparent 1.8px),
            radial-gradient(circle at 78% 7%, rgba(255,215,0,.75) 0 1px, transparent 1.8px),
            radial-gradient(circle at 94% 20%, rgba(183,44,255,.76) 0 1px, transparent 1.8px),
            radial-gradient(circle at 24% 48%, rgba(255,255,255,.45) 0 1px, transparent 1.8px),
            radial-gradient(circle at 68% 56%, rgba(255,215,0,.50) 0 1px, transparent 1.8px),
            radial-gradient(circle at 44% 84%, rgba(255,255,255,.42) 0 1px, transparent 1.8px);
          opacity:.58;
        }

        .roulette-hub-card {
          transition: transform .18s ease, box-shadow .18s ease, border-color .18s ease, filter .18s ease;
          -webkit-tap-highlight-color: transparent;
        }

        @media (hover: hover) {
          .roulette-hub-card:hover {
            transform: translateY(-3px);
            filter: saturate(1.08);
            border-color: rgba(218,165,32,.78) !important;
          }
        }

        .roulette-hub-card:focus-visible {
          outline: none;
          box-shadow: 0 0 0 3px rgba(218,165,32,.48), 0 18px 38px rgba(0,0,0,.38) !important;
        }

        @media (max-width: 520px) {
          #rouletteHubRoot .hub-shell {
            padding-left: 12px !important;
            padding-right: 12px !important;
          }
          #rouletteHubRoot .hub-hero {
            min-height: 218px !important;
          }
          #rouletteHubRoot .hub-wheel {
            width: 100% !important;
            height: 100% !important;
          }
          #rouletteHubRoot .hub-center {
            width: 92px !important;
            height: 92px !important;
            font-size: 1.02rem !important;
          }
        }

        @media (prefers-reduced-motion: reduce) {
          #rouletteHubRoot .hub-wheel { animation: none !important; }
          .roulette-hub-card { transition: none !important; }
        }
      `}</style>

      <div aria-hidden="true" style={styles.backgroundWheel} />

      <main className="hub-shell" style={styles.shell}>
        <section style={styles.topBar}>
          <button type="button" onClick={() => navigate(-1)} style={styles.topCircleButton} aria-label={t('common:actions.back')}>
            ←
          </button>
          <div style={styles.topTitleWrap}>
            <div style={styles.kicker}>{t('roulette.kicker')}</div>
            <h1 style={styles.title}>{t('roulette.title')}</h1>
            <p style={styles.topSubtitle}><span style={styles.onlineDot}>●</span> {t('roulette.screen.liveSubtitle')}</p>
          </div>
        </section>

        <section className="hub-hero" style={styles.heroCard}>
          <div aria-hidden="true" style={styles.heroGlow} />

          <div style={styles.heroWheelStage}>
            <div
              aria-hidden="true"
              className="hub-wheel"
              style={styles.heroWheel}
            />

            <div
              className="hub-center"
              style={styles.wheelCenterFixed}
              aria-hidden="true"
            >
              Zodiacus
            </div>
          </div>
        </section>

        <section style={styles.modeList} aria-label={t('roulette.screen.modeListAria')}>
          {rouletteModes.map((mode, index) => (
            <button
              key={mode.key}
              type="button"
              className="roulette-hub-card"
              onClick={handlers[mode.key]}
              style={styles.modeCard}
              autoFocus={index === 0}
              aria-label={t(`roulette.modes.${mode.key}.aria`)}
            >
              <span aria-hidden="true" style={styles.modeIcon}>{mode.icon}</span>
              <span style={styles.modeText}>
                <strong style={styles.modeTitle}>{t(`roulette.modes.${mode.key}.title`)}</strong>
                <small style={styles.modeSubtitle}>{t(`roulette.modes.${mode.key}.description`)}</small>
              </span>
              <span style={styles.modeCta}>{t(`roulette.modes.${mode.key}.cta`)} ›</span>
            </button>
          ))}
        </section>

        <section style={styles.howItWorks}>
          <div style={styles.howTitle}>{t('roulette.howItWorks')}</div>
          <div style={styles.stepsGrid}>
            <div style={styles.stepItem}>
              <span style={styles.stepIcon}>☸</span>
              <strong>{t('roulette.steps.chooseTitle')}</strong>
              <small>{t('roulette.steps.chooseText')}</small>
            </div>
            <div style={styles.stepItem}>
              <span style={styles.stepIcon}>👥</span>
              <strong>{t('roulette.steps.matchTitle')}</strong>
              <small>{t('roulette.steps.matchText')}</small>
            </div>
            <div style={styles.stepItem}>
              <span style={styles.stepIcon}>💬</span>
              <strong>{t('roulette.steps.chatTitle')}</strong>
              <small>{t('roulette.screen.liveChatStep')}</small>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}

const styles = {
  root: {
    minHeight: '100dvh',
    width: '100%',
    position: 'relative',
    isolation: 'isolate',
    overflowX: 'hidden',
    color: '#fff',
    background:
      'radial-gradient(circle at 50% -8%, rgba(218,165,32,.16), transparent 22%), radial-gradient(circle at 12% 18%, rgba(183,44,255,.25), transparent 34%), radial-gradient(circle at 84% 64%, rgba(138,43,226,.20), transparent 34%), linear-gradient(180deg, #03000b 0%, #100020 48%, #05000d 100%)',
  },
  backgroundWheel: {
    position: 'fixed',
    top: '50%',
    left: '50%',
    width: 'min(112svmin, 1180px)',
    height: 'min(112svmin, 1180px)',
    translate: '-50% -50%',
    backgroundImage: 'url("/logo/Zodiacus.jpeg")',
    backgroundSize: 'contain',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
    filter: 'drop-shadow(0 0 44px rgba(218,165,32,.22)) saturate(1.12)',
    borderRadius: '50%',
    animation: 'rouletteHubSpin 160s linear infinite',
    transformOrigin: 'center center',
    zIndex: 1,
    opacity: .18,
    pointerEvents: 'none',
  },
  shell: {
    position: 'relative',
    zIndex: 2,
    width: 'min(100%, 520px)',
    margin: '0 auto',
    paddingLeft: 14,
    paddingRight: 14,
    paddingTop: 'calc(env(safe-area-inset-top, 0px) + 14px)',
    paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 28px)',
    boxSizing: 'border-box',
  },
  topBar: {
    display: 'grid',
    gridTemplateColumns: '40px minmax(0, 1fr)',
    alignItems: 'center',
    gap: 9,
    marginBottom: 12,
    padding: '10px 10px',
    borderRadius: 24,
    border: '1px solid rgba(218,165,32,.32)',
    background: 'linear-gradient(145deg, rgba(7,0,19,.72), rgba(31,5,68,.50))',
    boxShadow: '0 18px 42px rgba(0,0,0,.34), inset 0 1px 0 rgba(255,255,255,.08)',
    backdropFilter: 'blur(16px) saturate(1.12)',
    WebkitBackdropFilter: 'blur(16px) saturate(1.12)',
  },
  topCircleButton: {
    width: 38,
    height: 38,
    minWidth: 38,
    borderRadius: '50%',
    border: '1px solid rgba(218,165,32,.44)',
    background: 'radial-gradient(circle at 34% 22%, rgba(255,255,255,.14), transparent 34%), rgba(10,0,26,.66)',
    color: '#FFE8A3',
    fontSize: 21,
    cursor: 'pointer',
    display: 'grid',
    placeItems: 'center',
    lineHeight: 1,
    padding: 0,
    boxShadow: '0 12px 28px rgba(0,0,0,.32)',
    boxSizing: 'border-box',
  },
  topTitleWrap: {
    minWidth: 0,
  },
  kicker: {
    color: '#F3C96B',
    fontSize: '.58rem',
    fontWeight: 950,
    letterSpacing: '.18em',
    textTransform: 'uppercase',
    marginBottom: 3,
  },
  title: {
    margin: 0,
    color: '#F3C96B',
    fontFamily: 'Georgia, serif',
    fontSize: 'clamp(1.65rem, 7.2vw, 2.35rem)',
    lineHeight: .96,
    letterSpacing: '.01em',
    textShadow: '0 0 24px rgba(218,165,32,.34)',
    whiteSpace: 'nowrap',
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  topSubtitle: {
    margin: '8px 0 0',
    color: 'rgba(255,255,255,.78)',
    fontSize: 13,
    fontWeight: 850,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  onlineDot: {
    color: '#27e38a',
    textShadow: '0 0 12px rgba(39,227,138,.85)',
    margin: '0 4px',
  },
  heroCard: {
    position: 'relative',
    display: 'grid',
    placeItems: 'center',
    minHeight: 238,
    marginBottom: 14,
    borderRadius: 24,
    overflow: 'hidden',
    border: '1px solid rgba(218,165,32,.32)',
    background: 'linear-gradient(145deg, rgba(7,0,19,.54), rgba(31,5,68,.34))',
    boxShadow: '0 18px 40px rgba(0,0,0,.32), inset 0 1px 0 rgba(255,255,255,.08)',
    backdropFilter: 'blur(12px) saturate(1.08)',
    WebkitBackdropFilter: 'blur(12px) saturate(1.08)',
  },
  heroGlow: {
    position: 'absolute',
    inset: 0,
    background: 'radial-gradient(circle at 50% 48%, rgba(183,44,255,.24), transparent 34%), radial-gradient(circle at 50% 82%, rgba(218,165,32,.10), transparent 32%)',
    pointerEvents: 'none',
  },
  heroWheelStage: {
    position: 'relative',
    width: 'min(72vw, 282px)',
    height: 'min(72vw, 282px)',
    display: 'grid',
    placeItems: 'center',
    zIndex: 1,
  },
  heroWheel: {
    width: '100%',
    height: '100%',
    borderRadius: '50%',
    backgroundImage: 'url("/logo/Zodiacus.jpeg")',
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'center center',
    backgroundSize: 'contain',
    opacity: .88,
    filter: 'drop-shadow(0 0 26px rgba(218,165,32,.34)) saturate(1.12)',
    animation: 'rouletteHubSpin 180s linear infinite',
    transformOrigin: 'center center',
  },
  wheelCenterFixed: {
    position: 'absolute',
    left: '50%',
    top: '50%',
    transform: 'translate(-50%, -50%)',
    width: 104,
    height: 104,
    borderRadius: '50%',
    display: 'grid',
    placeItems: 'center',
    background: 'radial-gradient(circle at 50% 28%, rgba(255,255,255,.18), transparent 36%), linear-gradient(145deg, rgba(138,43,226,.98), rgba(75,0,130,.98))',
    border: '2px solid rgba(218,165,32,.72)',
    boxShadow: '0 0 30px rgba(183,44,255,.34), 0 0 22px rgba(218,165,32,.18), inset 0 1px 0 rgba(255,255,255,.16)',
    color: '#F3C96B',
    fontFamily: 'Georgia, "Times New Roman", serif',
    fontWeight: 950,
    fontSize: '1.16rem',
    fontStyle: 'italic',
    letterSpacing: '.02em',
    textShadow: '0 0 16px rgba(218,165,32,.38), 0 2px 8px rgba(0,0,0,.45)',
    lineHeight: 1,
    zIndex: 3,
    pointerEvents: 'none',
  },
  modeList: {
    display: 'grid',
    gap: 11,
    marginBottom: 14,
  },
  modeCard: {
    width: '100%',
    borderRadius: 20,
    border: '1px solid rgba(218,165,32,.30)',
    background: 'linear-gradient(135deg, rgba(255,255,255,.08), rgba(255,255,255,.035))',
    color: '#fff',
    padding: 12,
    display: 'grid',
    gridTemplateColumns: '50px minmax(0, 1fr) auto',
    gap: 12,
    alignItems: 'center',
    cursor: 'pointer',
    textAlign: 'left',
    appearance: 'none',
    WebkitAppearance: 'none',
    font: 'inherit',
    boxShadow: '0 12px 28px rgba(0,0,0,.26), inset 0 1px 0 rgba(255,255,255,.06)',
    boxSizing: 'border-box',
    position: 'relative',
    overflow: 'hidden',
  },
  modeIcon: {
    width: 46,
    height: 46,
    borderRadius: '50%',
    border: '1px solid rgba(218,165,32,.38)',
    background: 'linear-gradient(145deg, rgba(138,43,226,.76), rgba(12,0,34,.88))',
    display: 'grid',
    placeItems: 'center',
    fontSize: 23,
    boxShadow: '0 10px 22px rgba(0,0,0,.26), 0 0 16px rgba(183,44,255,.18)',
  },
  modeText: {
    minWidth: 0,
    display: 'grid',
    gap: 4,
  },
  modeTitle: {
    fontSize: '1rem',
    color: '#fff',
    fontWeight: 950,
    lineHeight: 1.15,
  },
  modeSubtitle: {
    color: 'rgba(255,255,255,.75)',
    fontWeight: 700,
    lineHeight: 1.3,
    fontSize: '.78rem',
  },
  modeCta: {
    borderRadius: 999,
    padding: '8px 11px',
    border: '1px solid rgba(255,215,0,.40)',
    background: 'linear-gradient(135deg,#8A2BE2 0%, #6F1CCB 50%, #DAA520 100%)',
    color: '#fff',
    fontSize: '.74rem',
    fontWeight: 950,
    whiteSpace: 'nowrap',
    boxShadow: '0 10px 22px rgba(0,0,0,.28), 0 0 16px rgba(218,165,32,.18)',
  },
  howItWorks: {
    borderRadius: 22,
    padding: '14px 12px',
    border: '1px solid rgba(218,165,32,.28)',
    background: 'linear-gradient(145deg, rgba(7,0,19,.62), rgba(31,5,68,.42))',
    boxShadow: '0 14px 30px rgba(0,0,0,.26), inset 0 1px 0 rgba(255,255,255,.06)',
    backdropFilter: 'blur(12px) saturate(1.08)',
    WebkitBackdropFilter: 'blur(12px) saturate(1.08)',
  },
  howTitle: {
    textAlign: 'center',
    color: '#fff',
    fontWeight: 950,
    marginBottom: 14,
  },
  stepsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: 8,
  },
  stepItem: {
    minWidth: 0,
    textAlign: 'center',
    display: 'grid',
    gap: 5,
    color: '#fff',
  },
  stepIcon: {
    width: 42,
    height: 42,
    borderRadius: '50%',
    margin: '0 auto 3px',
    border: '1px solid rgba(218,165,32,.36)',
    background: 'rgba(138,43,226,.16)',
    display: 'grid',
    placeItems: 'center',
    color: '#F3C96B',
    fontSize: 19,
  },
};
